import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import './LanguageSwitcher.css';

const LanguageSwitcher = ({ variant = 'default' }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
    // Force a small delay to ensure the language change is processed
    setTimeout(() => {
      // Trigger a re-render by updating document direction
      document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    }, 100);
  };

  const isRTL = i18n.language === 'ar';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸', display: 'English' },
    { code: 'ar', name: 'عربي', flag: '🇸🇦', display: 'عربي' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  if (variant === 'dropdown' || variant === 'modern') {
    return (
      <div className="language-switcher-dropdown" ref={dropdownRef}>
        <button
          className="lang-dropdown-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t('language.selectLanguage', 'Select Language')}
          aria-expanded={isOpen}
        >
          <span className="lang-flag">{currentLanguage.flag}</span>
          <span className="lang-text">{currentLanguage.display}</span>
          <ChevronDown size={16} className={`lang-chevron ${isOpen ? 'open' : ''}`} />
        </button>
        {isOpen && (
          <div className="lang-dropdown-menu">
            {languages.map((lang) => (
              <button
                key={lang.code}
                className={`lang-dropdown-item ${i18n.language === lang.code ? 'active' : ''}`}
                onClick={() => changeLanguage(lang.code)}
                aria-label={t(`language.${lang.code === 'en' ? 'english' : 'arabic'}`)}
              >
                <span className="lang-flag">{lang.flag}</span>
                <span className="lang-text">{lang.display}</span>
                {i18n.language === lang.code && (
                  <span className="lang-check">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`language-switcher ${isRTL ? 'rtl' : 'ltr'}`}>
      <button
        className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
        onClick={() => changeLanguage('en')}
        title={t('language.english')}
        aria-label={t('language.english')}
      >
        English
      </button>
      <button
        className={`lang-btn ${i18n.language === 'ar' ? 'active' : ''}`}
        onClick={() => changeLanguage('ar')}
        title={t('language.arabic')}
        aria-label={t('language.arabic')}
      >
        عربي
      </button>
    </div>
  );
};

export default LanguageSwitcher;
